import { Collapse, Flex, Text } from '@mantine/core';
import { IconCalendar, IconChevronDown, IconX } from '@tabler/icons-react';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import BaseClickable from '@/components/ui/BaseClickable';
import { useLocalStore } from '@/hooks/useLocalStore';
import { OpenStore } from '@/stores/local/shared/OpenStore';
import { SearchDocumentStore } from '@/stores/local/sidebar/SearchDocumentStore';
import { DocumentTypesStore } from '@/stores/local/sidebar/SearchDocumentStore/DocumentTypesStore';

import PeriodColumn from './PeriodColumn';

import s from './SearchDocumentFilters.module.scss';

type SearchDocumentFiltersProps = {
  store: SearchDocumentStore;
};

const SearchDocumentFilters: React.FC<SearchDocumentFiltersProps> = ({ store }) => {
  const { documentTypesStore, periodStore } = store;
  const { isOpen, toggle } = useLocalStore(() => new OpenStore(false));
  const typesOpenStore = useLocalStore(() => new OpenStore(false));
  const periodOpenStore = useLocalStore(() => new OpenStore(false));

  React.useEffect(() => {
    documentTypesStore.load();
  }, [documentTypesStore]);

  const handleTypeToggle = React.useCallback(
    (key: string) => {
      store.toggleDocumentType(key);
    },
    [store],
  );

  const handleDateClear = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      periodStore.reset();
    },
    [periodStore],
  );

  const handleFiltersReset = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      store.resetFilters();
    },
    [store],
  );

  const isAllSelected = !documentTypesStore.hasSelection;
  const hasTypeSelection = documentTypesStore.hasSelection;
  const dateLabel = periodStore.label;
  const hasActiveFilters = store.hasActiveFilters;
  const activeFiltersCount = store.activeFiltersCount;
  const showFiltersHint = hasActiveFilters && !store.hasActiveSearch;

  return (
    <div className={s.root}>
      <BaseClickable
        element="div"
        block
        className={cn(
          s.root__header,
          isOpen && s.root__header_opened,
          hasActiveFilters && s['root__header_with-filters'],
        )}
        onClick={toggle}
      >
        <Flex align="center" gap={6} className={s['root__header-left']}>
          <Text fw={500} size="md">
            Фильтры
          </Text>
          {hasActiveFilters && (
            <span className={s.root__counter} aria-label={`Активных фильтров: ${activeFiltersCount}`}>
              {activeFiltersCount}
            </span>
          )}
          <BaseClickable
            className={cn(s.root__reset, !hasActiveFilters && s.root__reset_hidden)}
            onClick={handleFiltersReset}
            disabled={!hasActiveFilters}
          >
            <Text size="xs" fw={500}>
              Сбросить фильтры
            </Text>
          </BaseClickable>
        </Flex>
        <IconChevronDown size={16} stroke={1.5} className={cn(s.root__chevron, isOpen && s.root__chevron_opened)} />
      </BaseClickable>

      <Collapse in={isOpen} transitionDuration={120} transitionTimingFunction="ease-out" className={s.root__collapse}>
        <Flex className={s.root__group} gap="xs" wrap="wrap" align="center">
          {/* Кнопка «Тип документа» */}
          <BaseClickable
            className={cn(s.root__badge, (typesOpenStore.isOpen || hasTypeSelection) && s.root__badge_selected)}
            onClick={typesOpenStore.toggle}
          >
            <Text size="xs" fw={500}>
              Тип документа
            </Text>
          </BaseClickable>

          {/* Кнопка «Период документа» */}
          {periodStore.hasFilter && dateLabel !== null ? (
            <Flex
              align="center"
              gap={4}
              className={cn(s.root__badge, s.root__badge_selected, s['root__date-badge'])}
              onClick={periodOpenStore.toggle}
            >
              <IconCalendar size={12} />
              <Text size="xs" fw={500}>
                {dateLabel}
              </Text>
              <BaseClickable onClick={handleDateClear} className={s['root__date-clear']}>
                <IconX size={11} />
              </BaseClickable>
            </Flex>
          ) : (
            <BaseClickable
              className={cn(s.root__badge, periodOpenStore.isOpen && s.root__badge_selected)}
              onClick={periodOpenStore.toggle}
            >
              <Flex align="center" gap={4}>
                <IconCalendar size={12} />
                <Text size="xs" fw={500}>
                  Период документа
                </Text>
              </Flex>
            </BaseClickable>
          )}
        </Flex>

        {/* Раскрывающийся список типов */}
        <Collapse in={typesOpenStore.isOpen} transitionDuration={120}>
          <Flex className={s.root__types} wrap="wrap" gap="xs">
            <BaseClickable
              value={DocumentTypesStore.ALL_KEY}
              onClickWithValue={handleTypeToggle}
              className={cn(s.root__badge, isAllSelected && s.root__badge_selected)}
            >
              <Text size="xs" fw={500}>
                Все
              </Text>
            </BaseClickable>
            {documentTypesStore.types.map((type) => {
              const isSelected = documentTypesStore.isSelected(type.key);

              return (
                <BaseClickable
                  key={type.key}
                  value={type.key}
                  onClickWithValue={handleTypeToggle}
                  className={cn(s.root__badge, isSelected && s.root__badge_selected)}
                >
                  <Text size="xs" fw={500}>
                    {type.value}
                  </Text>
                </BaseClickable>
              );
            })}
          </Flex>
        </Collapse>

        {/* Раскрывающийся выбор периода */}
        <Collapse in={periodOpenStore.isOpen} transitionDuration={120}>
          <div className={s['root__period-section']}>
            <div className={s['root__period-cols']}>
              <PeriodColumn
                label="От"
                year={periodStore.fromYear}
                month={periodStore.fromMonth}
                maxYear={periodStore.toYear}
                maxMonth={periodStore.toMonth}
                onYearChange={periodStore.setFromYear}
                onMonthChange={periodStore.setFromMonth}
              />
              <div className={s['root__period-divider']} />
              <PeriodColumn
                label="До"
                year={periodStore.toYear}
                month={periodStore.toMonth}
                minYear={periodStore.fromYear}
                minMonth={periodStore.fromMonth}
                onYearChange={periodStore.setToYear}
                onMonthChange={periodStore.setToMonth}
              />
            </div>
          </div>
        </Collapse>

        {hasActiveFilters && (
          <div className={s['root__hint-slot']}>
            <div className={cn(s.root__hint, !showFiltersHint && s.root__hint_hidden)}>
              <Text size="xs">Фильтры применятся после ввода поискового запроса</Text>
            </div>
          </div>
        )}
      </Collapse>
    </div>
  );
};

export default observer(SearchDocumentFilters);
